/**
 * Global imperative dialogs. Registers usePlanUsageGuard callbacks; opens PlanUpgradeDialog
 * on usage_limit_exceeded, plan_expired, or no_plan.
 * Architectural Context: Feature provider. Mount once in App.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PlanUpgradeDialog } from '../dashboard/PlanUpgradeDialog';
import type { PlanUpgradeDialogConfig } from '../dashboard/PlanUpgradeDialog';
import { setUpgradeDialogCallback, setUsageLimitExceededHandler } from '../../hooks/usePlanUsageGuard';
import { useMyPlanSummary } from '../../hooks/useUsage';
import type { JobNotificationEvent } from '../../hooks/useJobNotifications';

const GUARDED_METRICS = ['cv', 'interview'] as const;

export const GlobalDialogs: React.FC = () => {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    config: PlanUpgradeDialogConfig;
    origin?: string;
  } | null>(null);

  const openUpgradeDialog = useCallback((params: {
    metric: 'cv' | 'interview';
    limit: number;
    used: number;
    origin?: string;
    reason?: 'plan_expired' | 'usage_limit_exceeded' | 'no_plan';
  }) => {
    const reason = params.reason ?? 'usage_limit_exceeded';

    if (reason === 'plan_expired' || reason === 'no_plan') {
      setDialogState({
        open: true,
        config: { variant: reason },
        origin: params.origin,
      });
    } else {
      setDialogState({
        open: true,
        config: {
          variant: 'usage_limit_exceeded',
          metric: params.metric,
          limit: params.limit,
          used: params.used,
        },
        origin: params.origin,
      });
    }
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setDialogState(prev => (prev ? { ...prev, open } : null));
  }, []);

  const handleUsageLimitExceeded = useCallback((event: JobNotificationEvent) => {
    if (event.event !== 'usage_limit_exceeded') return;
    const details = event.details || {};
    const metric = (details.metric as 'cv' | 'interview') || 'cv';
    const limit = details.limit as number || 0;
    const used = details.used as number || 0;
    openUpgradeDialog({
      metric,
      limit,
      used,
      origin: 'sse_notification',
      reason: 'usage_limit_exceeded',
    });
  }, [openUpgradeDialog]);

  useEffect(() => {
    setUpgradeDialogCallback(openUpgradeDialog);
    setUsageLimitExceededHandler(handleUsageLimitExceeded);
    return () => {
      setUpgradeDialogCallback(null);
      setUsageLimitExceededHandler(null);
    };
  }, [openUpgradeDialog, handleUsageLimitExceeded]);

  // Backstop for a missed `usage_limit_exceeded` SSE event (e.g. during a
  // reconnect gap): whenever the plan summary refreshes — including the
  // reconnect-triggered refetch in useJobNotifications.ts — open the same
  // dialog if a metric just crossed from having capacity to not having any.
  // Only fires on that transition, not on every refetch while already over,
  // so it can't spam the dialog open repeatedly.
  const { data: planSummary } = useMyPlanSummary();
  const wasOverLimitRef = useRef<Record<(typeof GUARDED_METRICS)[number], boolean>>({
    cv: false,
    interview: false,
  });

  useEffect(() => {
    if (!planSummary || !planSummary.has_plan || planSummary.expired) return;

    for (const metric of GUARDED_METRICS) {
      const limit = planSummary.limits[metric];
      const remaining = planSummary.remaining[metric];
      const isUnlimited = limit === -1 || remaining === -1;
      const isOverLimit = !isUnlimited && remaining !== null && remaining !== undefined && remaining <= 0;
      const wasOverLimit = wasOverLimitRef.current[metric];

      if (isOverLimit && !wasOverLimit) {
        openUpgradeDialog({
          metric,
          limit,
          used: planSummary.usage[metric],
          origin: 'usage_refetch',
          reason: 'usage_limit_exceeded',
        });
      }
      wasOverLimitRef.current[metric] = isOverLimit;
    }
  }, [planSummary, openUpgradeDialog]);

  if (!dialogState) return null;

  return (
    <PlanUpgradeDialog
      open={dialogState.open}
      onOpenChange={handleOpenChange}
      config={dialogState.config}
      origin={dialogState.origin}
    />
  );
};
